import { Text, Tooltip } from '@mantine/core';
import { neutralColors } from '@styles/design-system';
import { type FC } from 'react';

import { COLUMNS } from './cardColumns';

import styles from './JobCard.module.css';
import { formatCount } from '@utils/currency';

/**
 * The two pieces every job card renders its figures with, shared by both groupings.
 *
 * They were already separate components inside `JobCard`; all that changed is that they are
 * reachable from the SKU card too. Both still read `JobCard.module.css`, because a card grouped
 * by product is the same object as a card grouped by order — same geometry, same type, same
 * column widths — and giving it a second stylesheet would be two descriptions of one thing.
 */

/**
 * The labels, once, above everything they label.
 *
 * They used to sit UNDER the order row's figures, which made them read as that row's captions
 * rather than as headings for the columns beneath — seven words stacked below seven numbers, and
 * the eye had to work out which way the relationship ran. A header row says it without asking.
 */
export const ColumnHeader: FC = () => (
    <div className={styles.columnHeader}>
        <div />
        <div className={styles.numbers}>
            {COLUMNS.map((column) => (
                <Tooltip key={column.key} label={column.hint} multiline w={240}>
                    <Text
                        className={styles.columnLabel}
                        fz={10}
                        c={neutralColors.textSecondary}
                    >
                        {column.label}
                    </Text>
                </Tooltip>
            ))}
        </div>
        <div />
    </div>
);

interface NumberCellProps {
    value: number;
    hint: string;
    muted?: boolean;
    /** How much of the figure is FIFO-allocated shelf stock - marked, because a run and a shelf are different facts. */
    fromStock?: number;
}

export const NumberCell: FC<NumberCellProps> = ({ value, hint, muted, fromStock }) => (
    <Tooltip
        label={
            fromStock
                ? `${hint}. Includes ${formatCount(fromStock)} covered from stock already on hand, oldest order first - nothing is pegged, dispatching decides.`
                : hint
        }
        multiline
        w={240}
    >
        <div className={styles.number}>
            <Text
                className={styles.numberValue}
                fz={16}
                fw={600}
                c={muted ? neutralColors.textMuted : neutralColors.textPrimary}
            >
                {/* Grouped, as the cards above already are: 1250000 beside 1250000 ran together. */}
                {formatCount(value)}
                {fromStock ? (
                    <Text component="span" fz={12} c={neutralColors.textMuted}>
                        {' '}
                        *
                    </Text>
                ) : null}
            </Text>
        </div>
    </Tooltip>
);
